// src/hooks/useScrollToHash.ts
//
// Make `#section-id` URL hashes actually scroll the page to the matching
// element. React-Router v6 updates `location.hash` on navigate but doesn't
// scroll on its own. The alerts drawer relies on this so links like
// `/advisor/setup/wealth#liabilities` land the user directly on the
// LiabilitiesCard instead of the top of a dense page.
//
// Tolerates lazy-loaded routes: when the route's chunk hasn't rendered the
// section yet on first paint, we retry for up to ~1.2s before giving up.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_TRIES = 12;
const RETRY_INTERVAL_MS = 100;

export function useScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    if (!id) return;

    let tries = 0;
    let cancelled = false;

    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        // 'start' aligns the top of the section to the top of the viewport.
        // Sticky headers on subpages cover ~64px; the section's own padding
        // absorbs that gap so the heading is still visible.
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++tries < MAX_TRIES) {
        window.setTimeout(attempt, RETRY_INTERVAL_MS);
      }
    };

    // Small first delay so the route's lazy chunk has time to mount before
    // we start polling. Avoids a no-op first attempt on cold-navigate.
    const initial = window.setTimeout(attempt, 50);

    return () => {
      cancelled = true;
      window.clearTimeout(initial);
    };
    // Re-run when the pathname OR hash changes — same-page hash-only nav
    // (e.g. tapping a different alert that targets a different section of
    // the page we're already on) still needs to fire.
  }, [hash, pathname]);
}
